/**
 * One platen's "Small made from Large" line for one product (Phase 86 Round
 * 4, R4-12), shown ONCE per platen above By set's upload slots, beside
 * `LockedHint` as a shared product sync piece.
 *
 * `variant="compact"` (S6b, By colour): the same choice for ONE colour, in
 * the Large row's platen cell: "Small made from Large at 90%" with "Upload
 * Small by hand", or "Small uploaded by hand" with "Make Small from Large".
 * No box; the button's accessible name adds the colour and platen (several
 * colours share the words), and the status names the colour. `ownSmall`
 * (sides with the colour's own Small file) says that file is kept instead
 * of "made from Large" (and, for one side, that the other is made).
 *
 * Default: "14x16 Small: Made from Large · 90%" with "Upload Small by hand
 * instead". By hand: "14x16 Small: uploaded by hand for this product" with
 * "Make Small from Large", which switches back and makes every Small side
 * that can be made (`onMakeAll`). The cells keep only their own per-side
 * actions (Make Small from Large, the chip, making, failed, kept).
 *
 * Focus: the one button changes its words in place, so focus stays on it
 * after either switch. A polite, always-mounted status says what changed.
 * `blockedReason`: the switch cannot run now (By set: own Small files are
 * staged on the platen). The button stays mounted and focusable but
 * aria-disabled and a no-op, described by the reason shown in the box.
 * Presentation and focus only: the caller passes the choice and its setter.
 *
 * `SmallMadePanel` is the quiet panel an empty Small slot or cell shows
 * while its Small is made from Large: words only, no picker. A file dropped
 * on it is still handed to the caller (which turns Small by hand on once the
 * file passes its checks).
 */
import { useId, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { DtgPlatenCode } from '@threadheads/shared';
import { Button } from '@/components/ui/button';
import { NoteLine } from '@/components/shared/NoteLine';
import { cn } from '@/lib/utils';

/** The derive buttons wrap instead of clipping (a long zh label, a narrow cell). */
export const DERIVE_BUTTON_CLASS = 'h-auto min-h-7 whitespace-normal px-2.5 py-1 text-left text-[11px]';

interface SmallFromLargeLineProps {
  platen: DtgPlatenCode;
  /** The preset as a percent ("90"). */
  scalePercent: string;
  /** This product uploads its Small by hand on this platen. */
  byHand: boolean;
  /** Choose or drop by hand. Absent: the line says how Small is made, with no choice. */
  onByHand?: (on: boolean) => void;
  /** After switching back: make every Small side on this platen that can be made. */
  onMakeAll?: () => void;
  /** Only an admin can change the choice. */
  admin: boolean;
  /** Why the switch cannot run now (the button stays mounted, aria-disabled). */
  blockedReason?: string;
  /** `line`: the platen's boxed line (By set). `compact`: one colour, in its Large row (By colour). */
  variant?: 'line' | 'compact';
  /** `compact`: the colour row's name, for the button's name and the status. */
  colour?: string;
  /** `compact`: how many sides (0, 1 or 2) hold the colour's own Small file. */
  ownSmall?: number;
  className?: string;
}

export function SmallFromLargeLine({
  platen,
  scalePercent,
  byHand,
  onByHand,
  onMakeAll,
  admin,
  blockedReason,
  variant = 'line',
  colour = '',
  ownSmall = 0,
  className,
}: SmallFromLargeLineProps) {
  const { t } = useTranslation('productSync');
  const reasonId = useId();
  // Announced only after the operator switches, never on mount.
  const [announce, setAnnounce] = useState('');
  const compact = variant === 'compact';

  const toggle = () => {
    if (!onByHand || blockedReason) return;
    if (byHand) {
      onByHand(false);
      onMakeAll?.();
      setAnnounce(
        compact
          ? t('platen.derive.compact.nowMade', { platen, colour })
          : t('platen.derive.line.nowMade', { platen }),
      );
    } else {
      onByHand(true);
      setAnnounce(
        compact
          ? t('platen.derive.compact.nowByHand', { platen, colour })
          : t('platen.derive.line.nowByHand', { platen }),
      );
    }
  };

  if (compact) {
    const compactLabel = byHand ? t('platen.derive.make') : t('platen.derive.compact.choose');
    return (
      <div className={cn('flex min-w-0 flex-col items-center gap-1.5 text-center', className)}>
        <p className="text-[11px] leading-4 tabular-nums text-muted-foreground">
          {byHand
            ? t('platen.derive.compact.byHand')
            : ownSmall >= 2
              ? t('platen.derive.compact.own')
              : ownSmall === 1
                ? t('platen.derive.compact.ownOneSide', { pct: scalePercent })
                : t('platen.derive.compact.made', { pct: scalePercent })}
        </p>
        {admin && onByHand && (
          <Button
            variant="outline"
            size="sm"
            onClick={toggle}
            // The visible words first (label in name), then which colour and platen.
            aria-label={`${compactLabel} ${t('platen.derive.compact.which', { colour, platen })}`}
            className={cn(DERIVE_BUTTON_CLASS, 'text-center')}
          >
            {compactLabel}
          </Button>
        )}
        <span className="sr-only" role="status" aria-live="polite">
          {announce}
        </span>
      </div>
    );
  }

  return (
    <div
      className={cn(
        'flex flex-wrap items-center justify-between gap-x-3 gap-y-1 rounded-md border bg-muted/20 px-3 py-2',
        className,
      )}
    >
      <p className="min-w-0 text-[11px] tabular-nums text-foreground">
        {byHand
          ? t('platen.derive.line.byHand', { platen })
          : t('platen.derive.line.made', { platen, pct: scalePercent })}
      </p>
      {admin && onByHand && (
        <Button
          variant="outline"
          size="sm"
          onClick={toggle}
          aria-disabled={blockedReason ? true : undefined}
          aria-describedby={blockedReason ? reasonId : undefined}
          className={cn(DERIVE_BUTTON_CLASS, blockedReason && 'cursor-not-allowed opacity-60')}
        >
          {byHand ? t('platen.derive.make') : t('platen.derive.byHand.choose')}
        </Button>
      )}
      {admin && onByHand && blockedReason && (
        <NoteLine id={reasonId} className="basis-full text-[11px] leading-4">
          {blockedReason}
        </NoteLine>
      )}
      <span className="sr-only" role="status" aria-live="polite">
        {announce}
      </span>
    </div>
  );
}

interface SmallMadePanelProps {
  /** The panel's words, already translated, one line each. */
  lines: readonly ReactNode[];
  /** A file dropped on the panel (null: nothing usable was dropped). */
  onDropFile?: (file: File | null) => void;
  /** The minimum height class, so the panel matches its siblings (e.g. `min-h-[5.75rem]`). */
  minH: string;
  className?: string;
}

/** The quiet "Small is made from Large" panel: words only, drops handed to the caller. */
export function SmallMadePanel({ lines, onDropFile, minH, className }: SmallMadePanelProps) {
  const [dragOver, setDragOver] = useState(false);
  return (
    <div
      onDrop={
        onDropFile
          ? (e) => {
              e.preventDefault();
              setDragOver(false);
              onDropFile(e.dataTransfer.files?.[0] ?? null);
            }
          : undefined
      }
      onDragOver={
        onDropFile
          ? (e) => {
              e.preventDefault();
              setDragOver(true);
            }
          : undefined
      }
      onDragLeave={onDropFile ? () => setDragOver(false) : undefined}
      className={cn(
        'flex flex-col items-center justify-center gap-0.5 rounded-md border border-dashed px-3 py-4 text-center text-[11px] leading-4 text-muted-foreground transition-colors duration-200 motion-reduce:transition-none',
        minH,
        dragOver ? 'border-threadheads-purple bg-threadheads-purple/5' : 'bg-muted/20',
        className,
      )}
    >
      {lines.map((line, i) => (
        <span key={i}>{line}</span>
      ))}
    </div>
  );
}
